import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe,
  CheckCircle,
  AlertCircle,
  Copy,
  RefreshCw,
  ArrowLeft,
  Zap,
  Shield,
  ExternalLink,
  Link2,
} from "lucide-react";
import { useAppStore } from "../stores/appStore";
import { useTranslation } from "../i18n/useTranslation";
import { functionsService, isFirebaseConfigured } from "../services/firebase";
import { clsx } from "clsx";
import PublishPayment from "../components/PublishPayment";

// ─── Slug helpers ─────────────────────────────────────────────────────

function sanitizeSlug(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .substring(0, 40);
}

const RESERVED = new Set([
  "admin",
  "api",
  "login",
  "register",
  "gallery",
  "import",
  "editor",
  "connect",
  "dashboard",
  "security",
  "settings",
  "about",
  "help",
  "terms",
  "privacy",
  "index",
  "home",
  "www",
  "app",
  "sites",
  "s",
]);

// ─── Publish Step ─────────────────────────────────────────────────────

function PublishStep({ label, delay }: { label: string; delay: number }) {
  const [status, setStatus] = useState<"waiting" | "loading" | "done">(
    "waiting",
  );

  useEffect(() => {
    const t1 = setTimeout(() => setStatus("loading"), delay);
    const t2 = setTimeout(() => setStatus("done"), delay + 700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [delay]);

  return (
    <div className="flex items-center gap-2 text-sm">
      {status === "waiting" && (
        <div className="w-4 h-4 rounded-full border border-surface-300 flex-shrink-0" />
      )}
      {status === "loading" && (
        <RefreshCw
          size={16}
          className="animate-spin text-telegram flex-shrink-0"
        />
      )}
      {status === "done" && (
        <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
      )}
      <span
        className={
          status === "done"
            ? "text-green-700"
            : status === "loading"
              ? "text-telegram"
              : "text-surface-400"
        }
      >
        {label}
      </span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────

export default function ConnectPage() {
  const navigate = useNavigate();
  const { currentHtml, selectedTemplate, showToast, setMySites, mySites, user } =
    useAppStore();
  const { t } = useTranslation();

  const [siteName, setSiteName] = useState(
    selectedTemplate?.name || "Laman Saya",
  );
  const [customSlug, setCustomSlug] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [copied, setCopied] = useState(false);
  const publishSessionKey = `teleweb-publish-site-id:${user?.uid || "demo"}`;
  const [siteId] = useState(() => {
    const key = publishSessionKey;
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = `site-${crypto.randomUUID()}`;
    sessionStorage.setItem(key, id);
    return id;
  });
  const [paymentAllowed, setPaymentAllowed] = useState(!isFirebaseConfigured);
  const [paymentPopup, setPaymentPopup] = useState(false);

  // Auto-jana slug dari nama laman jika masih kosong
  const effectiveSlug = customSlug.trim()
    ? sanitizeSlug(customSlug)
    : sanitizeSlug(siteName);

  const slugError =
    effectiveSlug.length > 0 && effectiveSlug.length < 3
      ? "Nama minimum 3 aksara"
      : RESERVED.has(effectiveSlug)
        ? `"${effectiveSlug}" adalah reserved — pilih nama lain`
        : null;

  const handlePublish = async () => {
    if (isFirebaseConfigured && !paymentAllowed) {
      setPaymentPopup(true);
      return;
    }
    if (!currentHtml?.trim()) {
      showToast(t("connect.no_content"), "error");
      return;
    }
    if (slugError) {
      showToast(slugError, "error");
      return;
    }

    setIsPublishing(true);

    try {
      if (isFirebaseConfigured) {
        const billing = await functionsService.getBillingStatus({ siteId });
        if (!billing.canPublish) {
          setPaymentAllowed(false);
          setPaymentPopup(true);
          setIsPublishing(false);
          return;
        }

        const result = await functionsService.publishSite({
          siteId,
          siteName,
          htmlContent: currentHtml,
          templateId: selectedTemplate?.templateId,
          customSlug: effectiveSlug || undefined,
        });

        const newSite = {
          siteId,
          ownerId: "me",
          siteName,
          htmlContent: "",
          htmlSize: new Blob([currentHtml]).size,
          publicUrl: result.publicUrl,
          status: "published" as const,
          templateId: selectedTemplate?.templateId || null,
          templateName: selectedTemplate?.name,
          security: {
            passwordEnabled: false,
            passwordHash: null,
            rateLimitEnabled: true,
            rateLimitPerMinute: 100,
            ipWhitelistEnabled: false,
            ipWhitelist: [],
            ipBlacklistEnabled: false,
            ipBlacklist: [],
            captchaEnabled: false,
            expiryEnabled: false,
            expiryDate: null,
            botProtectionEnabled: true,
            securityScore: 45,
          },
          analytics: { totalViews: 0, uniqueVisitors: 0, lastViewAt: null },
          createdAt: new Date(),
          updatedAt: new Date(),
          publishedAt: new Date(),
          expiresAt: null,
        };
        setMySites([newSite, ...mySites]);
      } else {
        // Demo mode
        await new Promise((r) => setTimeout(r, 2200));
        const newSite = {
          siteId: `site-${Date.now()}`,
          ownerId: "demo",
          siteName,
          htmlContent: "",
          htmlSize: new Blob([currentHtml]).size,
          publicUrl: `https://teleweb.space/s/${effectiveSlug || "demo"}`,
          status: "published" as const,
          templateId: selectedTemplate?.templateId || null,
          templateName: selectedTemplate?.name,
          security: {
            passwordEnabled: false,
            passwordHash: null,
            rateLimitEnabled: true,
            rateLimitPerMinute: 100,
            ipWhitelistEnabled: false,
            ipWhitelist: [],
            ipBlacklistEnabled: false,
            ipBlacklist: [],
            captchaEnabled: false,
            expiryEnabled: false,
            expiryDate: null,
            botProtectionEnabled: true,
            securityScore: 45,
          },
          analytics: { totalViews: 0, uniqueVisitors: 0, lastViewAt: null },
          createdAt: new Date(),
          updatedAt: new Date(),
          publishedAt: new Date(),
          expiresAt: null,
        };
        setMySites([newSite, ...mySites]);
      }

      setIsPublishing(false);
      setIsPublished(true);
      sessionStorage.removeItem(publishSessionKey);
      showToast(t("connect.published_toast"), "success");
    } catch (err) {
      setIsPublishing(false);
      showToast(`❌ ${(err as Error).message || "Gagal publish"}`, "error");
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!currentHtml && !isPublished) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">📝</div>
          <h2 className="text-xl font-bold text-surface-900 mb-2">
            {t("connect.no_content")}
          </h2>
          <p className="text-surface-500 mb-6">{t("connect.no_content_sub")}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate("/gallery")}
              className="btn-primary btn-md"
            >
              {t("editor.choose_template")}
            </button>
            <button
              onClick={() => navigate("/import")}
              className="btn-outline btn-md"
            >
              {t("dash.import_html")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Published Success ────────────────────────────────────────────
  if (isPublished) {
    const site = mySites[0];
    const publicUrl =
      site?.publicUrl || `https://teleweb.space/s/${effectiveSlug || "demo"}`;

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle size={40} className="text-green-500" />
          </motion.div>

          <h2 className="text-2xl font-extrabold text-surface-900 mb-2">
            {t("connect.live_title")}
          </h2>
          <p className="text-surface-500 mb-8">{t("connect.live_sub")}</p>

          <div className="bg-surface-50 rounded-xl p-4 text-left mb-8">
            <div className="text-xs text-surface-500 mb-1 flex items-center gap-1">
              <Globe size={12} />
              {t("connect.web_url")}
            </div>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-surface-700 text-sm break-all">
                {publicUrl}
              </span>
              <button
                onClick={() => handleCopy(publicUrl)}
                className="p-1.5 hover:bg-surface-200 rounded-lg flex-shrink-0 ml-2"
              >
                {copied ? (
                  <CheckCircle size={14} className="text-green-500" />
                ) : (
                  <Copy size={14} />
                )}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary btn-md"
            >
              <Globe size={16} />
              {t("dash.open_site")}
            </a>
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-outline btn-md"
            >
              {t("nav.dashboard")}
            </button>
          </div>

          <button
            onClick={() => {
              setIsPublished(false);
              navigate("/security");
            }}
            className="mt-4 text-sm text-telegram hover:underline"
          >
            {t("connect.config_security")}
          </button>
        </motion.div>
      </div>
    );
  }

  // ─── Main Publish Form ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <div className="bg-white border-b border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Globe size={28} className="text-telegram" />
            <h1 className="text-3xl font-extrabold text-surface-900">
              {t("publish.title") || "Publish Laman"}
            </h1>
          </div>
          <p className="text-surface-500">
            {t("publish.sub") || "Beri nama custom untuk laman web anda"}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        {/* Site Name */}
        <div className="card p-6 mb-6">
          <h3 className="font-bold text-surface-900 mb-4 flex items-center gap-2">
            <Globe size={16} className="text-telegram" />
            {t("connect.site_info")}
          </h3>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1.5">
                {t("connect.site_name")}
              </label>
              <input
                type="text"
                value={siteName}
                onChange={(e) => {
                  setSiteName(e.target.value);
                  if (!customSlug.trim())
                    setCustomSlug(sanitizeSlug(e.target.value));
                }}
                placeholder={t("connect.site_name_ph")}
                className="input"
              />
              <p className="text-xs text-surface-400 mt-1.5">
                Nama paparan untuk laman anda (boleh ubah kemudian)
              </p>
            </div>

            {/* Custom Slug */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1.5 flex items-center gap-2">
                <Link2 size={14} className="text-telegram" />
                Nama Custom URL
              </label>
              <div className="flex items-stretch gap-0">
                <div className="flex items-center px-3 bg-surface-100 border border-r-0 border-surface-300 rounded-l-lg text-surface-500 text-sm font-mono select-none">
                  teleweb.space/s/
                </div>
                <input
                  type="text"
                  value={customSlug}
                  onChange={(e) => setCustomSlug(e.target.value)}
                  placeholder="kedai-baju-online"
                  className={clsx(
                    "input rounded-l-none flex-1 font-mono text-sm",
                    slugError && "input-error",
                  )}
                />
              </div>
              {slugError ? (
                <div className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {slugError}
                </div>
              ) : (
                <p className="text-xs text-surface-400 mt-1.5">
                  Laman akan boleh diakses di:{" "}
                  <span className="font-mono text-telegram font-medium">
                    https://teleweb.space/s/{effectiveSlug || "..."}
                  </span>
                </p>
              )}
              <p className="text-xs text-surface-400 mt-1">
                Hanya huruf kecil (a-z), nombor (0-9) dan dash (-)
              </p>
            </div>
          </div>
        </div>

        {isFirebaseConfigured && (
          <PublishPayment siteId={siteId} onStatus={setPaymentAllowed} />
        )}
        {paymentPopup && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onKeyDown={(e) => {
              if (e.key === "Escape") setPaymentPopup(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="payment-required-title"
              className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4"
            >
              <h2 id="payment-required-title" className="font-bold text-xl">
                Pengesahan bayaran diperlukan
              </h2>
              <p>
                Sila buat bayaran melalui QR atau kaedah alternatif di ruang
                bayaran. Publish hanya tersedia selepas admin mengesahkan
                bayaran anda.
              </p>
              <button
                autoFocus
                className="btn-primary btn-md"
                onClick={() => setPaymentPopup(false)}
              >
                Kembali ke ruang bayaran
              </button>
            </div>
          </div>
        )}
        {/* Publish Button */}
        <div className="card p-6 border-telegram/30 bg-gradient-to-br from-telegram/5 to-blue-50">
          <h3 className="font-bold text-surface-900 mb-4 flex items-center gap-2">
            <Zap size={16} className="text-telegram" />
            {t("connect.ready")}
          </h3>

          <div className="space-y-2 mb-6">
            {[
              { icon: CheckCircle, text: t("connect.ready_html") },
              {
                icon: Globe,
                text: `URL: teleweb.space/s/${effectiveSlug || "..."}`,
              },
              {
                icon: Shield,
                text: "Dilindungi dengan security headers automatik",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-surface-700"
              >
                <item.icon size={15} className="text-green-500 flex-shrink-0" />
                {item.text}
              </div>
            ))}
          </div>

          <button
            onClick={handlePublish}
            disabled={isPublishing || !!slugError}
            className={clsx(
              "btn-primary btn-lg w-full",
              !paymentAllowed && "opacity-60",
            )}
          >
            {isPublishing ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                {t("connect.publishing")}
              </>
            ) : (
              <>
                <Zap size={18} />
                {t("connect.publish_now")}
              </>
            )}
          </button>

          {isPublishing && (
            <div className="mt-4 space-y-2">
              {["Menyimpan HTML...", "Setup URL custom...", "Selesai!"].map(
                (step, i) => (
                  <PublishStep key={step} label={step} delay={i * 700} />
                ),
              )}
            </div>
          )}
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate("/editor")}
          className="btn-outline btn-md mt-6"
        >
          <ArrowLeft size={16} />
          {t("connect.back_editor")}
        </button>
      </div>
    </div>
  );
}
